"use client";

import { Fragment, useId, useState } from "react";
import Image from "next/image";

import { Separator } from "@radix-ui/react-separator";
import { ImageCarousel } from "@repo/ui-system/components/image-carousel";
import CheckIcon from "@repo/ui-system/icons/arrows/check/outline.svg";
import ChevronDownIcon from "@repo/ui-system/icons/arrows/chevron-down/outline.svg";
import ProIcon from "@repo/ui-system/icons/buttons/check/outline.svg";
import ConIcon from "@repo/ui-system/icons/buttons/minus/outline.svg";
import UsersIcon from "@repo/ui-system/icons/random/2-user/outline.svg";

import { buildRedirectUrl } from "@/lib/affiliate/build-redirect-url";
import type {
  BestReviews,
  BestStoreDeal,
} from "@/lib/api/best/keywords/schema";
import { BiEvent } from "@/lib/bi/bi.enum";
import { useLpvConfig } from "@/lib/lpv/lpv.context";
import { getStoreLogo } from "@/lib/store-logos";
import { fireOutboundConversion } from "@/lib/tracking/conversion";
import { cn } from "@/lib/utils";

import { OutboundLink } from "../../outbound-link/outbound-link";
import { Reviews } from "../reviews/reviews";
import { CommercialDebugOverlay } from "./commercial-debug-overlay";
import { formatPercentOff } from "./format-percent-off";
import {
  isDisplayableDiscount,
  StoreDealIndicator,
  useStoreDealActive,
} from "./store-deal";

type StoreProps = {
  name: string;
  url: string;
  logo?: string;
  price?: number;
  currency?: string;
  provider?: string;
  discount?: { value: number; percentage: number };
  deal?: BestStoreDeal;
};

type Props = {
  id: string;
  mainBadge: string;
  name: string;
  images: Array<{ url: string; alt: string }>;
  watcher: { month: number; week: number; count?: number } | null;
  reviews: BestReviews | null;
  whyChosen: string;
  whoFor: string;
  pros: string[];
  cons: string[];
  techSpecs: Array<{ label: string; value: string }>;
  stores: StoreProps[];
  /** Product detail URL when there are no retailer links or images. */
  productUrl: string;
  commission?: { maxCommission: number; minCommission: number } | null;
  isCC: boolean;
  attribution: { hasAttribution: boolean; commission: number } | null;
  cpc: number | null;
  position: number;
  lpv: string;
  isFirstItem?: boolean;
  className?: string;
};

/**
 * Store-specific variant of the best-product card (COM-681).
 *
 * Differs from BestProduct in two ways only:
 * 1. The product-level discount + limited-time-deal block under the title is
 *    removed.
 * 2. Each store row can render its own discount pill + deal indicator, sourced
 *    from that store's `discount`/`deal` fields.
 *
 * Everything else (image carousel, reviews, pros/cons, tech specs, expand
 * behaviour, BI) mirrors BestProduct so the swap is behaviour-preserving.
 */
export function BestProductStores({
  id,
  mainBadge,
  name,
  images,
  watcher,
  reviews,
  whyChosen,
  whoFor,
  pros,
  cons,
  techSpecs,
  stores,
  productUrl,
  commission,
  isCC,
  attribution,
  cpc,
  position,
  lpv,
  isFirstItem = false,
  className,
}: Props) {
  const headingId = useId();
  const mainBadgeId = useId();
  const primaryStore = stores[0];
  const primaryHref = primaryStore
    ? buildRedirectUrl(primaryStore.url, primaryStore.name, {
        eventType: BiEvent.SearchOutbound,
        id,
        provider: primaryStore.provider ?? primaryStore.name.toLowerCase(),
        seller: primaryStore.name,
        position,
        lpv,
      })
    : productUrl;
  const { productCard, overrideFirstBadge } = useLpvConfig();
  const isClosedLayout = productCard.layout === "closed";
  const [isExpanded, setIsExpanded] = useState(false);
  const showContent = !isClosedLayout || isExpanded;
  const expandedStore =
    stores.find((s) => s.name.toLowerCase() === "amazon") ??
    stores.find((s) => s.provider?.toLowerCase() === "amazon") ??
    stores[0]!;
  const expandedBi = {
    eventType: BiEvent.SearchOutbound as const,
    id,
    provider: expandedStore.provider ?? expandedStore.name.toLowerCase(),
    seller: expandedStore.name,
    position,
    sellerPosition: stores.indexOf(expandedStore) + 1,
    lpv,
  };
  const displayBadge =
    isFirstItem && overrideFirstBadge ? "Best Overall" : mainBadge;

  let popularityCount: number | null = null;
  if (watcher != null) {
    if (watcher.month >= 300) {
      popularityCount = watcher.month;
    }
  }

  return (
    <article
      id={id}
      className={cn("relative flex flex-col", className)}
      aria-labelledby={headingId}
      aria-describedby={mainBadgeId}
    >
      <div
        className={cn(
          "flex flex-col gap-y-4 gap-x-6 sm:grid sm:grid-cols-[1fr_1px_1fr]",
          popularityCount != null && "gap-y-2",
        )}
      >
        {/* Image Section */}
        <div className="relative flex flex-col gap-2 sm:gap-4">
          {/* Badges */}
          <OutboundLink
            url={productUrl}
            storeName="amazon"
            bi={expandedBi}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col gap-2"
          >
            <div className="flex items-start justify-between">
              <div className="flex gap-px">
                <div
                  className={cn(
                    "bg-badge-default text-badge-default-text ps-3 pe-2 py-1 rounded-l-lg",
                    isFirstItem && "bg-badge-first-bg text-badge-first-text",
                  )}
                >
                  <span className="text-base font-semibold leading-6">
                    {position + 1}
                  </span>
                </div>
                <div
                  className={cn(
                    "bg-badge-default text-badge-default-text ps-2 pe-3 py-1 rounded-r-lg",
                    isFirstItem && "bg-badge-first-bg text-badge-first-text",
                  )}
                >
                  <span
                    id={mainBadgeId}
                    aria-label={`promotion: ${displayBadge}`}
                    className="text-base font-semibold leading-6"
                  >
                    {displayBadge}
                  </span>
                </div>
              </div>
            </div>
          </OutboundLink>

          <div className="relative">
            {images.length > 0 ? (
              <ImageCarousel
                images={images}
                href={primaryHref}
                imageWidth={204}
                imageHeight={204}
                imageClassName="size-[184px] sm:size-[204px] object-contain"
                onLinkClick={fireOutboundConversion}
                renderImage={(props) => <Image {...props} />}
              />
            ) : (
              <OutboundLink
                url={productUrl}
                storeName="amazon"
                bi={expandedBi}
                target="_blank"
                rel="noopener noreferrer"
                className="grid place-items-center size-[184px] sm:size-[204px] rounded-lg border border-dashed border-border-grayscale-darker body2 text-text-grayscale-caption text-center px-4"
              >
                No images yet — open product page
              </OutboundLink>
            )}
            {position === 0 && popularityCount != null && (
              <div className="flex items-center justify-center gap-1 px-2 py-1 mt-2 body2 text-text-primary-title sm:ml-13">
                <UsersIcon
                  aria-hidden="true"
                  className="size-4 inline"
                />
                <span>
                  Picked by{" "}
                  <b>{popularityCount.toLocaleString("en-US")} users</b> this
                  week
                </span>
              </div>
            )}
          </div>
          <CommercialDebugOverlay
            price={
              primaryStore?.price != null && primaryStore.currency
                ? { value: primaryStore.price, currency: primaryStore.currency }
                : null
            }
            commission={commission ?? null}
            isCC={isCC}
            attribution={attribution}
            cpc={cpc}
          />
        </div>

        <Separator
          orientation="vertical"
          className="bg-border-grayscale-darker w-px hidden sm:block"
        />

        {/* Product Title + Stores */}
        <div className="flex flex-col justify-between">
          <OutboundLink
            url={productUrl}
            storeName="amazon"
            bi={expandedBi}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col gap-2"
          >
            <h5
              className="text-text-grayscale-subtitle"
              id={headingId}
            >
              {name}
            </h5>
          </OutboundLink>

          {/* Stores — per-store discount pill + deal indicator (COM-681) */}
          <div className="flex flex-col gap-2 mt-5 sm:mt-8">
            <div className="flex flex-col gap-4">
              {stores.map((store, index) => (
                <StoreRow
                  key={index}
                  store={store}
                  index={index}
                  id={id}
                  position={position}
                  lpv={lpv}
                />
              ))}
            </div>
            <p className="body3 font-light text-text-grayscale-body grid place-items-center">
              {stores.length > 0 ? (
                <>Click for pricing and details on the retailer&apos;s site</>
              ) : (
                <>Pricing may be available on the retailer&apos;s site</>
              )}
            </p>
          </div>
        </div>
      </div>

      <OutboundLink
        url={expandedStore.url}
        storeName={expandedStore.name}
        bi={expandedBi}
        target="_blank"
        rel="noopener noreferrer"
        className="block cursor-pointer"
        data-testid="expanded-outbound-link"
      >
        <div
          className={cn(
            "grid transition-[grid-template-rows,margin] duration-600 ease-in-out",
            showContent
              ? "grid-rows-[1fr] mt-4 sm:mt-8 lg:mt-5"
              : "grid-rows-[0fr] mt-0",
          )}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col gap-4 sm:gap-6">
              <Reviews reviews={reviews} />

              {/* Why/Who */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <p className="mini1 font-semibold text-[#111111]">
                    Why We Chose This Product:
                  </p>
                  <p className="body1 text-text-grayscale-body">{whyChosen}</p>
                </div>

                <div className="flex flex-col gap-1">
                  <p className="mini1 font-semibold text-[#111111]">
                    Who Is It For:
                  </p>
                  <p className="body1 text-text-grayscale-body">{whoFor}</p>
                </div>
              </div>

              {/* Pros/Cons */}
              <div className="border border-border-grayscale-darker rounded-xl p-4 flex flex-col gap-4">
                {pros.map((pro, index) => (
                  <div
                    key={index}
                    className="flex gap-2 items-start"
                  >
                    <ProIcon className="size-7 shrink-0 p-1 bg-surface-tertiary-subtle rounded-sm" />
                    <p className="body1 text-text-grayscale-body">{pro}</p>
                  </div>
                ))}
                {cons.map((con, index) => (
                  <div
                    key={index}
                    className="flex gap-2 items-start"
                  >
                    <ConIcon className="size-7 shrink-0 p-1 bg-surface-secondary-lighter rounded-sm" />
                    <p className="body1 text-text-grayscale-body">{con}</p>
                  </div>
                ))}
              </div>

              {techSpecs.length > 0 && (
                <div className="flex flex-col gap-2">
                  <p className="mini1 font-semibold text-[#111111]">
                    Tech specs:
                  </p>
                  <dl className="bg-surface-grayscale-default p-4 rounded-xl grid grid-cols-[20px_2fr_3fr] sm:grid-cols-[20px_1fr_2fr] gap-x-2 gap-y-1 min-w-0">
                    {techSpecs.map((spec, index) => (
                      <Fragment key={index}>
                        <CheckIcon className="w-5 h-5 mt-px shrink-0" />
                        <dt className="body1 text-text-grayscale-body after:content-[':'] min-w-0 break-words">
                          <span>{spec.label}</span>
                        </dt>
                        <dd className="mini1 text-text-grayscale-caption min-w-0 break-words">
                          {spec.value}
                        </dd>
                        {index < techSpecs.length - 1 && (
                          <Separator className="bg-border-grayscale-default col-span-3 h-px" />
                        )}
                      </Fragment>
                    ))}
                  </dl>
                </div>
              )}
            </div>
          </div>
        </div>
      </OutboundLink>

      {isClosedLayout && (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="mt-4 sm:mt-6 flex items-center justify-center gap-2 px-4 py-2 rounded-lg w-full bg-[#f6f7f9] sm:hover:bg-[#EAECF1] transition-colors"
        >
          <span className="mini1 font-semibold text-[#4b5665]">
            {isExpanded ? "Hide recommendations" : "View recommendations"}
          </span>
          <ChevronDownIcon
            className={cn(
              "size-5 text-[#4b5665] transition-transform duration-200",
              isExpanded && "rotate-180",
            )}
          />
        </button>
      )}
    </article>
  );
}

/**
 * A single store row: logo + "Check price" button, with an optional discount
 * box (discount pill + deal indicator) attached below when the store has a
 * discount and/or deal. Matches the Figma "Store diversity" component.
 */
function StoreRow({
  store,
  index,
  id,
  position,
  lpv,
}: {
  store: StoreProps;
  index: number;
  id: string;
  position: number;
  lpv: string;
}) {
  const discount = isDisplayableDiscount(store.discount)
    ? store.discount
    : null;
  const isDealActive = useStoreDealActive(store.deal);
  const deal = isDealActive ? store.deal : undefined;
  const hasPromoRow = discount != null || deal != null;

  return (
    <OutboundLink
      className="group block overflow-hidden"
      url={store.url}
      storeName={store.name}
      bi={{
        eventType: BiEvent.SearchOutbound,
        id,
        provider: store.provider ?? store.name.toLowerCase(),
        seller: store.name,
        position,
        sellerPosition: index + 1,
        lpv,
      }}
      target="_blank"
      rel="noopener noreferrer"
    >
      {/* Logo + CTA — single padded box: logo left, contained "Check price"
          pill right (12px inset each side), matching the Figma "CTA box". */}
      <div
        className={cn(
          "flex items-center justify-between gap-3 border border-border-grayscale-default rounded-xl px-3 py-2 overflow-hidden",
          hasPromoRow && "rounded-b-none",
        )}
      >
        <div className="flex min-w-0 items-center overflow-hidden">
          <StoreLogo store={store} />
        </div>
        <div className="grid place-items-center shrink-0 bg-(--cta-color,var(--color-cta)) sm:group-hover:bg-(--cta-color-hover,var(--color-cta-hover)) transition-colors text-grayscale-white font-semibold body1 rounded-lg px-5 py-2">
          Check price
        </div>
      </div>

      {/* Discount box (only when this store has a discount and/or deal) */}
      {hasPromoRow && (
        <div className="flex items-center gap-2 bg-surface-grayscale-default border border-t-0 border-border-grayscale-default rounded-b-xl px-3 py-1">
          {discount && (
            <span
              aria-label={`promotion: ${formatPercentOff(discount.percentage)}`}
              className="inline-flex items-center bg-badge-sale-bg text-badge-sale-text mini3 font-medium px-1 py-0.5 rounded-[4px]"
            >
              {formatPercentOff(discount.percentage)}
            </span>
          )}
          {deal && (
            <StoreDealIndicator
              type={deal.type}
              endTime={deal.endTime}
            />
          )}
        </div>
      )}
    </OutboundLink>
  );
}

function StoreLogo({ store }: { store: { name: string; logo?: string } }) {
  const logo = getStoreLogo(store);
  const [failed, setFailed] = useState(false);

  if (!logo || failed) {
    return <span className="max-w-full truncate">{store.name}</span>;
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={logo}
      alt={store.name}
      className="h-5 w-auto object-contain"
      onError={() => setFailed(true)}
    />
  );
}
